import type {
  Budget,
  BudgetAlertSeverity,
} from '@pflegenavi/frontend/api-nursing-home';

const SEVERITY_RANK: Record<BudgetAlertSeverity, number> = {
  info: 1,
  warning: 2,
  error: 3,
};

/**
 * Severity of the most severe currently-active alert on the budget, or null if
 * none are active. Drives the refined threshold coloring (info/warning/error).
 */
export function activeBudgetAlertSeverity(
  budget: Budget
): BudgetAlertSeverity | null {
  return (budget.alerts ?? [])
    .filter((alert) => alert.active)
    .reduce<BudgetAlertSeverity | null>(
      (max, alert) =>
        max === null || SEVERITY_RANK[alert.severity] > SEVERITY_RANK[max]
          ? alert.severity
          : max,
      null
    );
}

export function budgetConsumedCents(budget: Budget): number {
  return budget.consumption?.total ?? 0;
}

export function budgetAvailableCents(budget: Budget): number {
  return budget.budget_amount - budgetConsumedCents(budget);
}

/** Usage in percent (0-100+, may exceed 100). Infinity when a zero budget has consumption. */
export function budgetUsagePercent(budget: Budget): number {
  if (budget.budget_amount <= 0) {
    return budgetConsumedCents(budget) > 0 ? Infinity : 0;
  }
  return (budgetConsumedCents(budget) / budget.budget_amount) * 100;
}

export function isBudgetExceeded(budget: Budget): boolean {
  return budgetConsumedCents(budget) > budget.budget_amount;
}

export type BudgetStatus = 'exceeded' | BudgetAlertSeverity | 'ok';

/**
 * A budget's display status: 'exceeded' when overspent, otherwise the most
 * severe active alert severity, otherwise 'ok'. Single source of truth for the
 * status ladder; each platform maps the result onto its own palette.
 *
 * @example budgetStatus(budget) // 'warning'
 */
export function budgetStatus(budget: Budget): BudgetStatus {
  if (isBudgetExceeded(budget)) {
    return 'exceeded';
  }
  return activeBudgetAlertSeverity(budget) ?? 'ok';
}

/** Semantic color role, resolved to an actual color by each platform's theme. */
export type BudgetStatusTone = 'error' | 'warning' | 'info' | 'success';

const STATUS_TONE: Record<BudgetStatus, BudgetStatusTone> = {
  exceeded: 'error',
  error: 'error',
  warning: 'warning',
  info: 'info',
  ok: 'success',
};

/**
 * The tone a budget in this status should be painted in. Single source of truth
 * across web and mobile; each maps the tone onto its own palette.
 *
 * @example budgetStatusTone('exceeded') // 'error'
 */
export function budgetStatusTone(status: BudgetStatus): BudgetStatusTone {
  return STATUS_TONE[status];
}
